const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real `YYYY-MM-DD` calendar date, or null when the string is not one. */
export function parseIsoDate(value: string): string | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const yearText = match[1];
  const monthText = match[2];
  const dayText = match[3];
  if (!yearText || !monthText || !dayText) return null;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return null;
  }
  return value;
}

/** UTC calendar date, matching `publishedOn` in the updates file. */
export function utcDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function utcParts(isoDate: string): {
  year: number;
  month: number;
  day: number;
} {
  const parsed = parseIsoDate(isoDate);
  if (!parsed) {
    throw new Error(`Invalid ISO date: ${isoDate}`);
  }
  const [yearText, monthText, dayText] = parsed.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    throw new Error(`Invalid ISO date: ${isoDate}`);
  }
  return { year, month, day };
}

function formatUtcDate(
  isoDate: string,
  locale: string | undefined,
  month: 'short' | 'long'
): string {
  if (!parseIsoDate(isoDate)) return isoDate;
  const { year, month: monthNumber, day } = utcParts(isoDate);
  return new Intl.DateTimeFormat(locale, {
    month,
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthNumber - 1, day)));
}

export function formatUpdateDate(isoDate: string, locale?: string): string {
  return formatUtcDate(isoDate, locale, 'short');
}

export function formatDocsDate(isoDate: string): string {
  return formatUtcDate(isoDate, 'en-US', 'long');
}

/** Oldest on the left when the notice covers more than one day. */
export function formatUpdateDateRange(
  newest: string,
  oldest: string,
  locale?: string
): string {
  if (newest === oldest) return formatUpdateDate(newest, locale);
  return `${formatUpdateDate(oldest, locale)} – ${formatUpdateDate(newest, locale)}`;
}
